import { useState, MouseEvent, ChangeEvent } from 'react'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import EnhancedTableBodyTaskPool from './EnhancedTableBodyTaskPool'
import { HeadCell } from '@common/components/Table/table.types'
import { ReadTask } from '@common/config/api/client'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useParams } from 'react-router-dom'
import { useUserList } from '@features/reader_task_list_iam_view/pages/hooks/useUserList'
import { useSearch } from '@common/hooks/useSeach'
import ReaderTaskListBulkActionsTaskPool from './ReaderTaskListBulkActionsTaskPool'
import { useReadTasksManagementHandlers } from '../../hooks/useReadTasksManagementHandlers'
import { Stack, Typography, TableBody, TableRow, TableCell } from '@mui/material'
import { SPACING } from '@common/theme/spacing'

interface ReaderTaskListTableTaskPoolProps {
  subjectId?: string
}

const headCells: readonly HeadCell<ReadTask>[] = [
  { id: 'subject_id', disablePadding: false, label: 'Subject ID', disableSort: false },
  { id: 'blind_code', disablePadding: true, label: 'Blind code', disableSort: false },
  { id: 'timepoint_pseudo_name', disablePadding: false, label: 'Timepoint', disableSort: false },
  { id: 'task_pseudo_id', disablePadding: true, label: 'Task pseudo ID', disableSort: false },
  { id: 'task_type', disablePadding: false, label: 'Type', disableSort: false },
  { id: 'priority', disablePadding: false, label: 'Priority', disableSort: false },
  { id: 'status', disablePadding: false, label: 'Status', disableSort: false },
  { id: 'created_at', disablePadding: false, label: 'Created at', disableSort: false },
  { id: 'due_date', disablePadding: true, label: 'Due date', disableSort: false },
  { id: 'assignee', disablePadding: true, label: 'Assignee', disableSort: true },
]

function EmptyReaderTaskPoolTable({ searchValue }: { searchValue?: string }) {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">{searchValue ? 'No matching tasks found' : 'No tasks available in pool'}</Typography>
      <Typography variant="body2" color="text.secondary">
        {searchValue
          ? 'Try adjusting your search criteria or clear the search field'
          : 'There are currently no unassigned reader tasks in the pool'}
      </Typography>
    </Stack>
  )
}

export default function ReaderTaskListTableTaskPool({ subjectId }: ReaderTaskListTableTaskPoolProps) {
  const [selected, setSelected] = useState<readonly (string | null | undefined)[]>([])
  const { user } = useExtendedAuth()
  const { studyId } = useParams()
  const { value } = useSearch()

  const {
    order,
    orderBy,
    paginationParams,
    data,
    error,
    isLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
  } = useReadTasksManagementHandlers({
    category: 'task_pool',
    studyId: studyId!,
    subjectId,
    searchValue: value,
  })

  const {
    data: userListData,
    error: userListError,
    isLoading: UserlistLoading,
  } = useUserList(['ReaderTasksList', 'Users'], { study_id: studyId! })

  if (isLoading || UserlistLoading) {
    return <div>Loading...</div>
  }
  if (error || userListError) {
    return <div>Error: {(error && error.message) || (userListError && userListError.message)}</div>
  }

  const hasPoolTasks = data?.data?.items && data.data.items.length > 0

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data?.data?.items?.map((n: ReadTask) => n.task_id)
      if (newSelected && newSelected.length > 0) {
        setSelected(newSelected)
      }
      return
    }
    setSelected([])
  }

  const handleClick = (_event: MouseEvent<unknown>, id?: string | null) => {
    const selectedIndex = selected.indexOf(id)
    let newSelected: readonly (string | null | undefined)[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }
    setSelected(newSelected)
  }

  const selectedTasks = selected.map(
    (taskId) => data?.data?.items?.find((task) => task.task_id === taskId)?.task_id || '',
  )

  return (
    <EnhancedTable
      rows={data?.data?.items || []}
      headCells={headCells}
      order={order}
      orderBy={orderBy.split('.')[0] as keyof ReadTask}
      selected={selected}
      rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
      rowsPerPage={paginationParams.limit || 0}
      next_cursor={data?.data?.pagination.next_cursor}
      previous_cursor={data?.data?.pagination.previous_cursor}
      onRequestSort={handleRequestSort}
      onSelectAllClick={handleSelectAllClick}
      onChangePage={handlePageChange}
      bulkActions={
        <ReaderTaskListBulkActionsTaskPool
          taskIds={selectedTasks}
          study_id={studyId!}
          assigneeList={userListData?.data?.users || []}
        />
      }
      onChangeRowsPerPage={handleRowsPerPageChange}
    >
      {hasPoolTasks ? (
        <EnhancedTableBodyTaskPool
          rows={data?.data?.items || []}
          order={order}
          orderBy={orderBy}
          selected={selected}
          handleClick={handleClick}
          assigneeList={userListData?.data?.users || []}
          authedUserId={user?.profile.sub}
        />
      ) : (
        <TableBody>
          <TableRow>
            <TableCell colSpan={headCells.length}>
              <EmptyReaderTaskPoolTable searchValue={value} />
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </EnhancedTable>
  )
}
